import { Search } from '@mui/icons-material';
import { Box, InputAdornment, TextField } from '@mui/material';
import { useDebouncedChangeHandler } from 'app/common/hooks/use-debounced-change-handler';
import type { SearchForm as SearchFormValues } from 'app/universal-search/types';
import type React from 'react';
import { useEffect } from 'react';
import { type UseControllerProps, useController, useForm } from 'react-hook-form';

export type SearchFormProps = {
  readonly query?: string;
  readonly onSubmit: (data: SearchFormValues) => void;
};

const SearchInput: React.FC<UseControllerProps<SearchFormValues, 'query'>> = (props) => {
  const {
    field: { ref, ...field }
  } = useController(props);
  return (
    <TextField
      variant="outlined"
      {...field}
      value={field.value ?? ''}
      ref={ref}
      placeholder="Search"
      style={{ flex: '1 1 auto' }}
      autoFocus
      inputProps={{
        'data-testid': 'universal-search-input'
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        )
      }}
    />
  );
};

const SearchForm: React.FC<SearchFormProps> = ({ query, onSubmit }) => {
  const { control, watch, handleSubmit } = useForm<SearchFormValues>();
  const debouncedSubmit = useDebouncedChangeHandler(onSubmit, 500);

  useEffect(() => {
    const watcher = watch(async () => {
      await handleSubmit(debouncedSubmit.run)();
    });
    return () => {
      watcher.unsubscribe();
    };
  }, [watch, handleSubmit, debouncedSubmit]);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} width="100%" display="flex">
      <SearchInput control={control} name="query" defaultValue={query} />
    </Box>
  );
};

export default SearchForm;
